import React, { useEffect, useCallback } from 'react';
import {
  Box,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Divider,
  Badge,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SetSelector from './SetSelector';
import RotationProgress from './RotationProgress';
import { CameraSet } from '../../types/canvision.types';

const DWELL_OPTIONS = [
  { value: 5, label: '5s' },
  { value: 10, label: '10s' },
  { value: 15, label: '15s' },
  { value: 20, label: '20s' },
  { value: 30, label: '30s' },
  { value: 60, label: '60s' },
];

interface VideoWallControlsProps {
  sets: CameraSet[];
  selectedSetId: number | null;
  onSetChange: (setId: number | null) => void;
  dwellTime: number;
  onDwellTimeChange: (seconds: number) => void;
  isPlaying: boolean;
  onPlay: () => void;
  onPause: () => void;
  onNext: () => void;
  onPrevious: () => void;
  onGoToSet: (index: number) => void;
  currentSetIndex: number;
  currentSet: CameraSet | null;
  timeRemaining: number;
  disabled?: boolean;
  // Fullscreen props
  isFullscreen?: boolean;
  isFullscreenSupported?: boolean;
  onToggleFullscreen?: () => void;
  // Alert props
  alertCount?: number;
  onToggleAlerts?: () => void;
}

function VideoWallControls({
  sets,
  selectedSetId,
  onSetChange,
  dwellTime,
  onDwellTimeChange,
  isPlaying,
  onPlay,
  onPause,
  onNext,
  onPrevious,
  onGoToSet,
  currentSetIndex,
  currentSet,
  timeRemaining,
  disabled,
  isFullscreen = false,
  isFullscreenSupported = true,
  onToggleFullscreen,
  alertCount = 0,
  onToggleAlerts,
}: VideoWallControlsProps) {
  // Keyboard shortcuts
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Don't trigger if user is typing in an input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }

      switch (e.code) {
        case 'Space':
          e.preventDefault();
          if (isPlaying) {
            onPause();
          } else {
            onPlay();
          }
          break;
        case 'ArrowRight':
          e.preventDefault();
          onNext();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          onPrevious();
          break;
        case 'KeyF':
          e.preventDefault();
          onToggleFullscreen?.();
          break;
      }
    },
    [isPlaying, onPlay, onPause, onNext, onPrevious, onToggleFullscreen]
  );

  useEffect(() => {
    if (disabled) return;

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown, disabled]);

  const canRotate = sets.length > 1 && selectedSetId !== null;

  return (
    <Box>
      {/* Top row: Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {/* Set selector */}
        <SetSelector
          sets={sets}
          selectedSetId={selectedSetId}
          onSetChange={onSetChange}
          disabled={disabled}
        />

        <Divider orientation="vertical" flexItem />

        {/* Rotation controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Previous set (Left Arrow)">
            <span>
              <IconButton
                size="small"
                onClick={onPrevious}
                disabled={disabled || !canRotate}
              >
                <SkipPreviousIcon />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={isPlaying ? 'Pause (Space)' : 'Play (Space)'}>
            <span>
              <IconButton
                size="small"
                onClick={isPlaying ? onPause : onPlay}
                disabled={disabled || !canRotate}
                color={isPlaying ? 'primary' : 'default'}
              >
                {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title="Next set (Right Arrow)">
            <span>
              <IconButton
                size="small"
                onClick={onNext}
                disabled={disabled || !canRotate}
              >
                <SkipNextIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Divider orientation="vertical" flexItem />

        {/* Dwell time selector */}
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel id="dwell-time-label">Dwell</InputLabel>
          <Select
            labelId="dwell-time-label"
            value={dwellTime}
            onChange={(e) => onDwellTimeChange(e.target.value as number)}
            label="Dwell"
            disabled={disabled}
          >
            {DWELL_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Fullscreen button */}
        {isFullscreenSupported && onToggleFullscreen && (
          <>
            <Divider orientation="vertical" flexItem />
            <Tooltip title={isFullscreen ? 'Exit fullscreen (F)' : 'Fullscreen (F)'}>
              <IconButton
                size="small"
                onClick={onToggleFullscreen}
                disabled={disabled}
                color={isFullscreen ? 'primary' : 'default'}
              >
                {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Tooltip>
          </>
        )}

        {/* Alert badge */}
        {onToggleAlerts && (
          <>
            <Divider orientation="vertical" flexItem />
            <Tooltip title={`${alertCount} alert${alertCount !== 1 ? 's' : ''}`}>
              <IconButton
                size="small"
                onClick={onToggleAlerts}
                disabled={disabled}
                color={alertCount > 0 ? 'warning' : 'default'}
              >
                <Badge
                  badgeContent={alertCount}
                  color="error"
                  max={99}
                >
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          </>
        )}
      </Box>

      {/* Bottom row: Progress indicator (only when a set is selected) */}
      {selectedSetId !== null && sets.length > 0 && (
        <Box sx={{ mt: 2, pt: 1, borderTop: 1, borderColor: 'divider' }}>
          <RotationProgress
            currentSetIndex={currentSetIndex}
            totalSets={sets.length}
            currentSet={currentSet}
            isPlaying={isPlaying}
            timeRemaining={timeRemaining}
            dwellTime={dwellTime}
            onDotClick={onGoToSet}
          />
        </Box>
      )}
    </Box>
  );
}

export default VideoWallControls;
